import * as React from "react";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

export interface ComboboxOption<T> {
  key: string;
  label: React.ReactNode;
  value: T;
}

interface ComboboxProps<T> {
  id?: string;
  value: T | null;
  displayValue: string;
  placeholder?: string;
  inputClassName?: string;
  loading?: boolean;
  disabled?: boolean;
  options: ComboboxOption<T>[];
  /** Called as the user types. */
  onQueryChange: (q: string) => void;
  /** Called when the user picks an option (or clears the selection). */
  onSelect: (value: T | null, option: ComboboxOption<T> | null) => void;
  /** Optional renderer for an empty-results state. */
  emptyMessage?: React.ReactNode;
  /** Minimum query length before opening the dropdown. Defaults to 1; pass 0 to open on focus. */
  minChars?: number;
}

export function Combobox<T>({
  id,
  value,
  displayValue,
  placeholder,
  inputClassName,
  loading = false,
  disabled = false,
  options,
  onQueryChange,
  onSelect,
  emptyMessage = "No matches",
  minChars = 1,
}: ComboboxProps<T>) {
  const [query, setQuery] = React.useState(displayValue);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Keep input in sync when the parent changes the selected value externally.
  React.useEffect(() => {
    setQuery(displayValue);
  }, [displayValue]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [options]);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const showDropdown =
    !disabled &&
    open &&
    (query.trim().length >= minChars || loading) &&
    (loading || options.length > 0 || emptyMessage !== null);

  const commit = (opt: ComboboxOption<T>) => {
    onSelect(opt.value, opt);
    setQuery(typeof opt.label === "string" ? opt.label : displayValue);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        className={cn("pr-8", inputClassName)}
        value={query}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onQueryChange(next);
          setOpen(true);
          if (value !== null && next !== displayValue) {
            onSelect(null, null);
          }
        }}
        onFocus={() => {
          if (query.trim().length >= minChars || minChars === 0) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, options.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            const opt = options[activeIndex];
            if (opt) {
              e.preventDefault();
              commit(opt);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {query && !disabled && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Clear"
          onClick={() => {
            setQuery("");
            onSelect(null, null);
            onQueryChange("");
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {showDropdown && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-card shadow-md">
          {loading && options.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            options.map((opt, i) => (
              <button
                key={opt.key}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={cn(
                  "block w-full cursor-pointer px-3 py-2 text-left text-sm",
                  i === activeIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50"
                )}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
